import { propExists } from './utils';


/**
 * Serialise a grid of cells to CSV text — the exact format the Staff Site's
 * on-screen export download and the scheduled emailed-reports Node service both
 * emit. Each cell is trimmed of surrounding whitespace; a cell whose text could
 * be read as a spreadsheet formula is prefixed with a single quote (CSV/formula
 * injection defence); a cell is blank when absent; a cell that contains the
 * delimiter, a double quote, or a line break is wrapped in double quotes with any
 * embedded quotes doubled (per RFC 4180); rows are joined with CRLF.
 *
 * This is the framework-agnostic core of `generateCsvDownload` (src/app/utils.ts),
 * which wraps the result in a Blob for the browser. Keeping the serialisation
 * here means the emailed CSV and the downloaded CSV cannot drift apart.
 */
export function rowsToCsv(content: any[][]): string {
  const rowStrs: string[] = content.map(
    row => row
      .map(cell => {
        if (!propExists(cell)) { return ''; }
        // Guard against non-string cells (e.g. numbers) — matches prior behaviour.
        // Trim surrounding whitespace, including the stray leading/trailing
        // newlines that some saved form-option labels carry, so they can't
        // surface as blank lines inside a cell (or, unquoted, split the record).
        let value: string = ((cell.indexOf === undefined) ? cell.toString() : cell).trim();
        // Neutralise spreadsheet formula injection (OWASP CSV injection): exports
        // include user-controlled text, and a cell beginning with a formula
        // trigger can execute when opened in Excel/Sheets. Prefix a single quote,
        // but leave plain numbers alone so negative amounts (-50.00) aren't
        // mangled. (Leading tab/CR triggers are already removed by the trim above.)
        if (/^[=+\-@]/.test(value) && !/^-?\d+(\.\d+)?$/.test(value)) {
          value = "'" + value;
        }
        // A field must be quoted if it contains the delimiter, a double quote, or
        // a line break (CR or LF) — see RFC 4180. Quoting on commas alone let a
        // value with an embedded newline split a record across physical CSV rows.
        if (/[",\r\n]/.test(value)) {
          // Wrap in quotes, doubling any existing quotes.
          return '"' + value.replace(/"/g, '""') + '"';
        }
        return value;
      })
      .join(',')
  );
  return rowStrs.join('\r\n');
}
